import { PropsWithChildren } from "react";
import { useEffect } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { ProductItemCategoryKey } from "~/api/models/ProductCategory";
import { useFetchOpportunity } from "~/api/salesForce/useFetchOpportunity";
import { useFetchProductCategories } from "~/api/storyblok/useFetchProductCategories";
import { ErrorElement } from "~/components/organisms/ErrorElement/ErrorElement";
import ScreenLoader from "~/components/organisms/ScreenLoader/ScreenLoader";
import useFlattenedProducts from "~/hooks/useFlattenedProducts";
import useInitializeProducts from "~/hooks/useInitializeProducts";
import useOpportunityProducts from "~/hooks/useOpportunityProducts";
import { useCheckoutStore } from "~/stores/checkout/useCheckoutStore";
import useSelectedProductCategoriesStore from "~/stores/selectedProductCategories/useSelectedProductCategoriesStore";

type InitializedProps = PropsWithChildren;

const Initialized = ({ children }: InitializedProps) => {
  const intl = useIntl();
  const [
    specialConditionsValues,
    otherSpecialConditions,
    discountCode,
    referralCode,
    individualDiscountAmount,
    individualDiscountPercent,
    setPaymentMethod,
    setSpecialConditionsValues,
    setOtherSpecialConditions,
    setDiscountCode,
    setReferralCode,
    setIndividualDiscountAmount,
  ] = useCheckoutStore((state) => [
    state.specialConditionsValues,
    state.otherSpecialConditions,
    state.discountCode,
    state.referralCode,
    state.individualDiscountAmount,
    state.individualDiscountPercent,
    state.setPaymentMethod,
    state.setSpecialConditionsValues,
    state.setOtherSpecialConditions,
    state.setDiscountCode,
    state.setReferralCode,
    state.setIndividualDiscountAmount,
  ]);
  const { data: HSDropdownOptions, isLoading: loadingHSDropdownOptions } =
    useFetchProductCategories();
  const [setSelectedProductCategories, selectedProductCategories] =
    useSelectedProductCategoriesStore((state) => [
      state.setSelectedProductCategories,
      state.selectedProductCategories,
    ]);
  const { opportunityId } = useParams();
  const {
    data: opportunity,
    isLoading: loadingOpportunity,
    isError: opportunityError,
  } = useFetchOpportunity({
    id: opportunityId as string,
  });
  const { getCategoriesByOpportunityProducts } =
    useOpportunityProducts(opportunity);
  const { isLoading: loadingProducts } = useFlattenedProducts();

  useEffect(() => {
    if (
      !loadingHSDropdownOptions &&
      HSDropdownOptions &&
      !loadingOpportunity &&
      !loadingProducts
    ) {
      const data = HSDropdownOptions.map((group) =>
        group.options?.length > 0 ? group.options : group
      ).flat();
      if (!selectedProductCategories.length) {
        const categoriesByOpportunityProducts =
          getCategoriesByOpportunityProducts(data);
        if (
          categoriesByOpportunityProducts &&
          categoriesByOpportunityProducts.length
        ) {
          setSelectedProductCategories(categoriesByOpportunityProducts);
        } else {
          // If there are no categories by opportunity products, we set the initial product item categories
          // Intial product item categories are HT and PRP
          // We also need make sure that the opportunity has no products or bundles before setting the initial product item categories (TEMP solution)
          if (
            opportunity?.products.length === 0 &&
            opportunity?.bundles.length === 0
          ) {
            const initialProductItemCategories = data.filter(
              (category) =>
                category.key === ProductItemCategoryKey.ht ||
                category.key === ProductItemCategoryKey.prp
            );
            setSelectedProductCategories(initialProductItemCategories);
          }
        }
      }
    }
  }, [
    HSDropdownOptions,
    loadingHSDropdownOptions,
    setSelectedProductCategories,
    loadingOpportunity,
    loadingProducts,
    selectedProductCategories.length,
    getCategoriesByOpportunityProducts,
  ]);

  useEffect(() => {
    if (opportunity?.paymentMeans) {
      setPaymentMethod(opportunity.paymentMeans);
    }
  }, [opportunity?.paymentMeans, setPaymentMethod]);

  useEffect(() => {
    if (opportunity?.specialConditions && specialConditionsValues === null) {
      setSpecialConditionsValues(opportunity.specialConditions);
    }
  }, [
    opportunity?.specialConditions,
    setSpecialConditionsValues,
    specialConditionsValues,
  ]);

  useEffect(() => {
    if (
      opportunity?.otherSpecialConditions &&
      otherSpecialConditions === null
    ) {
      setOtherSpecialConditions(opportunity.otherSpecialConditions);
    }
  }, [
    opportunity?.otherSpecialConditions,
    setOtherSpecialConditions,
    otherSpecialConditions,
  ]);

  useEffect(() => {
    if (opportunity?.discountCode && discountCode === null) {
      setDiscountCode(opportunity.discountCode);
    }
  }, [opportunity?.discountCode, setDiscountCode]);

  useEffect(() => {
    if (opportunity?.referralCode && referralCode === null) {
      setReferralCode(opportunity.referralCode);
    }
  }, [opportunity?.referralCode, setReferralCode]);

  useEffect(() => {
    if (
      opportunity?.discountTotal &&
      opportunity?.discountCode === null &&
      individualDiscountAmount === null &&
      individualDiscountPercent === null
    ) {
      setIndividualDiscountAmount(opportunity.discountTotal);
    }
  }, [opportunity?.discountTotal, setIndividualDiscountAmount]);

  useInitializeProducts({ opportunity, loadingOpportunity });

  return (
    <>
      {loadingOpportunity ? (
        <ScreenLoader />
      ) : !opportunity || opportunityError ? (
        <ErrorElement
          hideButtons
          customTitle={intl.formatMessage({
            id: "opportunity.error.title",
          })}
          customDescription={intl.formatMessage({
            id: "opportunity.error.description",
          })}
        />
      ) : (
        children
      )}
    </>
  );
};

export default Initialized;
